import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  CheckCheck,
  Clock,
  FileText,
  Gavel,
  Search,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  apiClient,
  type Notification,
  type NotificationTemplateKey,
} from '@/lib/api-client';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

type ReadFilter = 'all' | 'unread';

const TEMPLATE_ICONS: Record<string, typeof Bell> = {
  'intimacao.new': FileText,
  'monitored_term.matched': Search,
  'deadline.approaching': Clock,
  'invite.accepted': Users,
  'client.new': Gavel,
};

function iconForTemplate(key: string): typeof Bell {
  return TEMPLATE_ICONS[key] ?? Bell;
}

/**
 * F05 — Pagina completa de notificacoes.
 *
 * Sidebar de filtros (todas / nao lidas / por templateKey) + lista paginada.
 * Click em linha navega para `link` (se existir) e marca como lida.
 */
export default function NotificacoesPage() {
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [templateFilter, setTemplateFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const pageSize = 25;

  const listQuery = useQuery({
    queryKey: ['notifications', 'list', 'full', readFilter, page],
    queryFn: () =>
      apiClient.notificationsList(
        readFilter === 'unread',
        pageSize,
        page * pageSize,
      ),
    staleTime: 5_000,
  });

  const catalogQuery = useQuery({
    queryKey: ['notifications', 'templates-catalog'],
    queryFn: () => apiClient.notificationsTemplatesCatalog(),
    staleTime: 5 * 60_000,
  });

  const markAsReadMut = useMutation({
    mutationFn: (id: string) => apiClient.notificationsMarkAsRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMut = useMutation({
    mutationFn: () => apiClient.notificationsMarkAllAsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const allItems = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;

  // Filtragem client-side por templateKey (backend ja filtra por read/unread).
  const filteredItems = useMemo(() => {
    if (!templateFilter) return allItems;
    return allItems.filter((n) => n.templateKey === templateFilter);
  }, [allItems, templateFilter]);

  const templates = catalogQuery.data?.items ?? [];

  function handleClick(n: Notification) {
    if (!n.readAt) markAsReadMut.mutate(n.id);
    if (n.link) navigate(n.link);
  }

  const hasNext = (page + 1) * pageSize < total;

  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
      <aside
        className="w-full shrink-0 md:w-60"
        aria-label="Filtros de notificações"
      >
        <nav>
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
            Status
          </p>
          <ul className="mb-4 flex flex-col gap-0.5">
            {(['all', 'unread'] as const).map((f) => (
              <li key={f}>
                <button
                  type="button"
                  onClick={() => {
                    setReadFilter(f);
                    setPage(0);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                    readFilter === f
                      ? 'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]'
                      : 'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                  )}
                >
                  {f === 'all' ? 'Todas' : 'Não lidas'}
                </button>
              </li>
            ))}
          </ul>

          {templates.length > 0 && (
            <>
              <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
                Por tipo
              </p>
              <ul className="flex flex-col gap-0.5">
                <li>
                  <button
                    type="button"
                    onClick={() => setTemplateFilter(null)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                      templateFilter === null
                        ? 'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]'
                        : 'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                    )}
                  >
                    Todos os tipos
                  </button>
                </li>
                {templates.map((t) => {
                  const Icon = iconForTemplate(t.templateKey);
                  const active = templateFilter === t.templateKey;
                  return (
                    <li key={t.templateKey}>
                      <button
                        type="button"
                        onClick={() => setTemplateFilter(t.templateKey)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                          active
                            ? 'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]'
                            : 'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{t.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </nav>
      </aside>

      <div className="min-w-0 flex-1 space-y-4">
        <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-[var(--fg)] md:text-2xl">
              Notificações
            </h1>
            <p className="mt-1 text-sm text-[var(--fg-muted)]">
              Tudo o que aconteceu no seu escritório, em ordem cronológica.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => markAllAsReadMut.mutate()}
            disabled={markAllAsReadMut.isPending || filteredItems.length === 0}
          >
            <CheckCheck className="mr-2 h-4 w-4" />
            Marcar todas como lidas
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
          {listQuery.isLoading ? (
            <p className="px-4 py-12 text-center text-sm text-[var(--fg-muted)]">
              Carregando...
            </p>
          ) : listQuery.isError ? (
            <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
              <Bell className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
              <p className="text-sm text-[var(--fg-muted)]">
                Não foi possível carregar suas notificações no momento.
              </p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
              <Bell className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
              <p className="text-sm text-[var(--fg-muted)]">
                {readFilter === 'unread'
                  ? 'Nenhuma notificação não lida.'
                  : 'Nenhuma notificação por enquanto.'}
              </p>
            </div>
          ) : (
            <ul className="flex flex-col">
              {filteredItems.map((n) => {
                const Icon = iconForTemplate(n.templateKey);
                const unread = !n.readAt;
                return (
                  <li
                    key={n.id}
                    className="border-b border-[var(--border)] last:border-b-0"
                  >
                    <button
                      type="button"
                      onClick={() => handleClick(n)}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-left transition',
                        'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]',
                        unread &&
                          'bg-[color-mix(in_oklch,var(--accent)_4%,transparent)]',
                      )}
                    >
                      <div
                        className={cn(
                          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                          unread
                            ? 'bg-[color-mix(in_oklch,var(--accent)_15%,transparent)] text-[var(--accent)]'
                            : 'bg-[var(--surface-muted)] text-[var(--fg-muted)]',
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <p
                            className={cn(
                              'truncate text-sm',
                              unread
                                ? 'font-semibold text-[var(--fg)]'
                                : 'font-medium text-[var(--fg)]',
                            )}
                          >
                            {n.title}
                          </p>
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-[var(--fg-muted)]">
                            {formatRelativeTime(n.createdAt)}
                          </span>
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-xs text-[var(--fg-muted)]">
                          {n.body}
                        </p>
                        {n.link && (
                          <p className="mt-1 text-[11px] font-medium text-[var(--accent)]">
                            Abrir →
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {!listQuery.isError && (page > 0 || hasNext) && (
          <div className="flex items-center justify-between text-xs text-[var(--fg-muted)]">
            <span>
              Mostrando {page * pageSize + 1}-
              {Math.min((page + 1) * pageSize, total)} de {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNext}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
